import React, { useEffect, useState } from 'react';
import { Input, Select, Table, Tooltip } from 'antd';
import { Button as CrcButton, Spinner } from '@crc-evans/ui-components';
import { EditOutlined, DeleteOutlined, SearchOutlined, ToolOutlined } from '@ant-design/icons';

/** Debounce toolbar search so typing triggers server filter without pressing Enter. */
export const useDebouncedSearch = (initialValue = '', delay = 350) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [debouncedValue, setDebouncedValue] = useState(initialValue);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(inputValue), delay);
    return () => clearTimeout(timer);
  }, [inputValue, delay]);

  return [inputValue, debouncedValue, setInputValue];
};

/** Shared Select props — light dropdown panel (matches Equipment / Projects modals). */
export const awSelectProps = {
  dropdownClassName: 'heat-profile-select-dropdown',
  getPopupContainer: () => document.body,
  dropdownMatchSelectWidth: true,
  listHeight: 256,
};

/** Projects-style search bar (teal focus ring, icon left). */
export const AwFilterSearch = ({ value, onChange, placeholder, className = '' }) => (
  <div className={`aw-filter-control aw-filter-control--search aw-toolbar-search ${className}`.trim()}>
    <SearchOutlined className="aw-toolbar-search-icon" aria-hidden />
    <input
      type="search"
      className="aw-toolbar-search-input"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

/** Toolbar / filter dropdown — same height & border as search. */
export const AwFilterSelect = ({
  value,
  onChange,
  placeholder = 'All',
  allowClear = true,
  showSearch = false,
  optionFilterProp,
  disabled = false,
  children,
}) => {
  const resolvedValue =
    value === '' || value == null ? undefined : String(value);

  return (
    <div className="aw-filter-control aw-filter-control--select">
      <Select
        {...awSelectProps}
        className="aw-toolbar-select"
        placeholder={placeholder}
        allowClear={allowClear}
        showSearch={showSearch}
        optionFilterProp={optionFilterProp}
        disabled={disabled}
        value={resolvedValue}
        onChange={(v) => onChange(v ?? '')}
      >
        {children}
      </Select>
    </div>
  );
};

const mergeClassNames = (...parts) => parts.filter(Boolean).join(' ');

/** Normalize string or { value, label, key, id, name } entries for Select options. */
export const normalizeSelectOptions = (options = []) =>
  options
    .map((opt) => {
      if (opt == null) return null;
      if (typeof opt === 'string' || typeof opt === 'number') {
        const str = String(opt);
        return { value: str, label: str };
      }
      if (typeof opt === 'object') {
        const rawValue = opt.value ?? opt.key ?? opt.id ?? opt.siteCode ?? opt.code;
        if (rawValue == null || rawValue === '') return null;
        const value = String(rawValue);
        const label = String(
          opt.label
          ?? opt.name
          ?? opt.siteName
          ?? opt.projectName
          ?? opt.profileName
          ?? value
        );
        return { value, label };
      }
      return null;
    })
    .filter(Boolean);

/** Match form value to option value (string compare avoids number/string mismatch). */
export const resolveSelectValue = (value, options = []) => {
  if (value == null || value === '') return undefined;
  const str = String(value);
  const normalized = normalizeSelectOptions(options);
  return normalized.some((o) => o.value === str) ? str : undefined;
};

/** Ensure current value appears in the list so the closed select can render a label. */
export const mergeSelectOptions = (options = [], value, fallbackLabel) => {
  const normalized = normalizeSelectOptions(options);
  if (value == null || value === '') return normalized;
  const str = String(value);
  if (normalized.some((o) => o.value === str)) return normalized;
  return [{ value: str, label: fallbackLabel ? String(fallbackLabel) : str }, ...normalized];
};

export const AwModalSelect = ({ className = '', ...props }) => (
  <Select
    {...awSelectProps}
    className={mergeClassNames('aw-modal-select', className)}
    {...props}
  />
);

/**
 * Form select — always renders Ant Select (dropdown) when options exist;
 * falls back to free-text Input only when options array is empty.
 */
export const AwFormSelect = ({
  options = [],
  value,
  onChange,
  onAfterChange,
  placeholder = 'Select',
  className = '',
  allowClear = true,
  showSearch = true,
  optionFilterProp = 'label',
  disabled = false,
  loading = false,
  fallbackToInput = false,
  valueLabel,
  filterOption,
  ...rest
}) => {
  const displayOptions = React.useMemo(
    () => mergeSelectOptions(options, value, valueLabel),
    [options, value, valueLabel]
  );

  const resolvedValue = React.useMemo(
    () => resolveSelectValue(value, displayOptions),
    [value, displayOptions]
  );

  const defaultFilterOption = React.useCallback((input, option) => {
    const label = option?.label ?? option?.children ?? '';
    return String(label).toLowerCase().includes(String(input ?? '').toLowerCase());
  }, []);

  const handleChange = (v) => {
    const val = v ?? undefined;
    onChange?.(val);
    onAfterChange?.(val);
  };

  if (!displayOptions.length && fallbackToInput) {
    return <Input placeholder={placeholder} disabled={disabled} value={value} onChange={onChange} {...rest} />;
  }

  return (
    <Select
      {...awSelectProps}
      className={mergeClassNames('aw-modal-select', 'aw-form-select', className)}
      placeholder={placeholder}
      allowClear={allowClear}
      showSearch={showSearch}
      optionFilterProp={optionFilterProp}
      filterOption={filterOption ?? defaultFilterOption}
      disabled={disabled}
      loading={loading}
      notFoundContent={loading ? 'Loading…' : 'No options'}
      value={resolvedValue}
      onChange={handleChange}
      {...rest}
    >
      {displayOptions.map((opt) => (
        <Select.Option key={opt.value} value={opt.value} label={opt.label}>
          {opt.label}
        </Select.Option>
      ))}
    </Select>
  );
};

export const AwFilterGroup = ({ label, children, className = '' }) => (
  <div className={`heat-profile-filter-group aw-filter-group ${className}`.trim()}>
    {label && <span className="heat-profile-filter-label">{label}</span>}
    {children}
  </div>
);

/**
 * Shared list layout — matches Calibration / Materials embedded modern layout.
 */
const ArcWeldingListShell = ({
  title,
  description,
  count,
  headerActions = [],
  filterBar = null,
  columns,
  dataSource,
  rowKey = 'id',
  loading = false,
  pagination = false,
  scroll = { x: 1200 },
  onRow,
  rowSelection,
  children = null,
}) => {
  const itemCount = count ?? dataSource?.length ?? 0;

  return (
    <div className="heat-profile-card aw-list-shell">
      <div className="heat-profile-list-header">
        <div className="heat-profile-header-left">
          <div className="heat-profile-icon-wrap">
            <ToolOutlined style={{ fontSize: 22 }} />
          </div>
          <div className="heat-profile-header-content">
            <div className="heat-profile-title-row">
              <h3 className="heat-profile-list-title">{title}</h3>
              <span className="heat-profile-summary">
                {itemCount} item{itemCount !== 1 ? 's' : ''}
              </span>
            </div>
            {description && (
              <p className="heat-profile-list-description">{description}</p>
            )}
          </div>
        </div>
        {headerActions.length > 0 && (
          <div className="heat-profile-header-actions calibration-modern-actions">
            {headerActions.map(({ key: actionKey, label, ...action }) => (
              <CrcButton key={actionKey || label} label={label} {...action} size="small" />
            ))}
          </div>
        )}
      </div>

      {filterBar && <div className="aw-list-filter-bar">{filterBar}</div>}

      <div className="addTeamDetails heat-profile-table-wrap">
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey={rowKey}
          onRow={onRow}
          rowSelection={rowSelection}
          loading={{
            indicator: <Spinner variant="inline" size="medium" />,
            spinning: loading,
          }}
          className="h-100 pipeline-table equipPaginationTable heat-profile-table"
          scroll={scroll}
          pagination={pagination}
        />
      </div>

      {children}
    </div>
  );
};

export const buildTextColumn = (key, label, { width, render, filterValue, onFilterChange } = {}) => ({
  title: filterValue != null && onFilterChange ? (
    <div className="column-header-with-filter">
      <div className="column-header-text-wrapper">
        <span>{label}</span>
      </div>
      <div className="header-filter-input">
        <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell">
          <Input
            placeholder="Search"
            size="small"
            style={{ width: '120px' }}
            value={filterValue}
            onChange={(e) => onFilterChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    </div>
  ) : (
    label
  ),
  dataIndex: key,
  key,
  align: 'left',
  width: width || Math.max(label.length * 10 + 60, 140),
  render: render || ((val) => (val === undefined || val === null || val === '' ? '—' : String(val))),
});

export const buildActionsColumn = ({
  onEdit,
  onDelete,
  canDelete,
  deleteDisabledTooltip = 'Make equipment active',
}) => ({
  title: 'Actions',
  key: 'actions',
  align: 'center',
  width: 120,
  fixed: 'right',
  render: (_, record) => {
    const deleteEnabled = !canDelete || canDelete(record);
    return (
      <div className="heat-profile-actions-cell">
        {onEdit && (
          <Tooltip title="Edit">
            <EditOutlined className="txtIcon" onClick={() => onEdit(record)} />
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title={deleteEnabled ? 'Delete' : deleteDisabledTooltip}>
            <DeleteOutlined
              className={`txtIcon${deleteEnabled ? '' : ' txtIcon--disabled'}`}
              onClick={() => {
                if (!deleteEnabled) return;
                onDelete(record);
              }}
            />
          </Tooltip>
        )}
      </div>
    );
  },
});

export const buildServerPagination = ({
  total,
  pageNumber,
  pageSize,
  onChange,
  className = 'heat-profile-pagination aw-pagination',
}) => (
  total > 0
    ? {
        position: ['bottomRight'],
        className,
        showSizeChanger: true,
        total,
        current: pageNumber,
        pageSize,
        pageSizeOptions: ['10', '20', '50', '100'],
        showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} items`,
        locale: { items_per_page: '/ page' },
        onChange: (page, size) => onChange(page, size || pageSize),
      }
    : false
);

export const buildClientPagination = ({
  total,
  pageNumber,
  pageSize,
  onChange,
}) => buildServerPagination({ total, pageNumber, pageSize, onChange });

export default ArcWeldingListShell;
